import { WithBusyIndicator, withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import Header from "@components/header";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import { IProps as ISmartTableProps } from "@components/smart/smartTable";
import { getRow } from "@components/smart/smartTable/SmartTable.utils";
import { IColumn } from "@components/table";
import { IRowProps } from "@components/table/Rows.types";
import { TToolbarItem } from "@components/toolbar/Toolbar.types";
import { getDetailRouteByEntityType, getEntityTypeFromBindingContext } from "@odata/EntityTypes";
import { EntitySetName, EntityTypeName, IAssetEntity, IUnorganizedAssetEntity } from "@odata/GeneratedEntityTypes";
import { AssetTypeCode } from "@odata/GeneratedEnums";
import { ButtonGroupStyled } from "@pages/payroll/Eldp/Eldp.styles";
import { getCompanyCurrency, isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import { joinReactNodes } from "@utils/dom.utils";
import { Dayjs } from "dayjs";
import i18next from "i18next";
import React, { ReactElement } from "react";
import { ValidationError } from "yup";

import { BreadCrumbProvider } from "../../../components/breadCrumb";
import Clickable from "../../../components/clickable";
import CollapsibleSection from "../../../components/collapsibleSection/CollapsibleSection";
import ReadOnlyList from "../../../components/readOnlyList/ReadOnlyList";
import { ScrollBar } from "../../../components/scrollBar";
import SimpleTable from "../../../components/simpleTable";
import { NEW_ITEM_DETAIL } from "../../../constants";
import { WithAuthContext, withAuthContext } from "../../../contexts/authContext/withAuthContext";
import { QueryParam, Status, TextAlign, ToolbarItemType } from "../../../enums";
import { ColoredText, ToolbarStaticText } from "../../../global.style";
import { IModifierKeys, TValue } from "../../../global.types";
import BindingContext, { createBindingContext } from "../../../odata/BindingContext";
import { getQueryParameters } from "../../../routes/Routes.utils";
import { formatCurrency } from "../../../types/Currency";
import { getUtcDayjs } from "../../../types/Date";
import memoizeOneWithParamsCheck from "../../../utils/memoizeOne";
import { FormStorage } from "../../../views/formView/FormStorage";
import PlugAndPlayForm from "../../../views/formView/PlugAndPlayForm";
import TableView from "../../../views/table";
import {
    ICustomButtonDefArgs,
    TableActionOrder,
    TableButtonsAction,
    TableButtonsActionType,
    TTableToolbarItem
} from "../../../views/table/TableToolbar.utils";
import { ITableViewBaseProps } from "../../../views/table/TableView";
import { getConfirmationActionText } from "../../../views/table/TableView.render.utils";
import { SecondaryTableViewTitle, TopWrapper } from "../../../views/table/TableView.styles";
import View from "../../../views/View";
import { getDefByEntityType } from "../../getDefByEntityType";
import { setMatchingNumberRange } from "../../numberRange/NumberRange.utils";
import {
    createAssetFromEntries,
    getInActiveFYValidation,
    getMaxJournalEntryDateValidation,
    getValidationResult,
    pairDocumentItemsWithAsset
} from "../Asset.utils";
import { FixedAssetFormViewAction, IFixedAssetCustomData } from "../fixedAsset/FixedAsset.utils";
import { assetPriceFormatter, FixedAssetEditableWindowActionFinish } from "../fixedAsset/FixedAssetDef";
import { getAddToCostsDefFactory } from "./dialogs/AddToCostsDef";
import AddToCostsDialogFormView from "./dialogs/AddToCostsDialogFormView";
import CompleteFixedAsset from "./dialogs/CompleteFixedAsset";
import CreateNewAssetDialog from "./dialogs/CreateNewAssetDialog";


enum UnorganizedAssetAction {
    Create = "Create",
    Complete = "Complete",
    AddToCosts = "AddToCosts"
}

interface IProps extends ITableViewBaseProps, WithAuthContext, WithBusyIndicator {
    title?: string;
    buttons?: React.ReactElement;
    summary?: React.ReactElement;
}

class UnorganizedAssetTableView extends TableView<IProps> {
    _actionDialogRef = React.createRef<any>();
    _selectedRows: IUnorganizedAssetEntity[];
    _amount = 0;

    static defaultProps: Partial<IProps> = {
        hideDrilldown: true
    };

    constructor(props: IProps) {
        super(props);

        this.getToolbarButtons = this.getToolbarButtons.bind(this);
        this.getCustomButtonDef = this.getCustomButtonDef.bind(this);
        this.handleCustomAction = this.handleCustomAction.bind(this);
        this.getActiveButtons = this.getActiveButtons.bind(this);
        this.getDisabledButtons = this.getDisabledButtons.bind(this);
        // this.presetDefaultFilters = this.presetDefaultFilters.bind(this);

        let defaultTab: AssetTypeCode;

        if (this.isInEditableWindow && this.rootStorageAssetType) {
            this.setTableAction(UnorganizedAssetAction.Complete);
        }

        if (!this.isCbaCompany) {
            defaultTab = getQueryParameters()[QueryParam.DefaultTab] as AssetTypeCode ?? this.rootStorageAssetType ?? AssetTypeCode.Tangible;

            this.state = {
                ...this.state,
                selectedSecondaryFilter: defaultTab
            };
            this.applySecondaryFilter(defaultTab);
        }
    }

    get shouldRenderBreadcrumbs(): boolean {
        return !this.isInEditableWindow;
    }

    handleRowSelect = (bindingContext: BindingContext, props: IRowProps, modifiers?: IModifierKeys): void => {
        // no row selection on UnorganizedAsset
    };

    getToolbarButtons(): TableButtonsActionType[] {
        const buttons = super.getToolbarButtons();
        const filteredButtons = buttons.filter(id => ![TableButtonsAction.Add, TableButtonsAction.Remove].includes(id as TableButtonsAction));

        let assetActions = [
            UnorganizedAssetAction.Create,
            UnorganizedAssetAction.Complete
        ];

        if (!this.isCbaCompany) {
            assetActions.push(UnorganizedAssetAction.AddToCosts);
        }

        if (this.isInEditableWindow) {
            // show only AddTo action in editableWindow
            assetActions = assetActions.filter(id => id === UnorganizedAssetAction.Complete);
        }

        return [...assetActions, ...filteredButtons];
    }

    getDisabledButtons(disableAll?: boolean): TableButtonsActionType[] {
        const disabledButtons = super.getDisabledButtons(disableAll);
        const action = this.getTableAction();
        const unorganizedButtons = [
            UnorganizedAssetAction.Create,
            UnorganizedAssetAction.Complete,
            UnorganizedAssetAction.AddToCosts
        ].filter(button => button !== action);
        const isEmpty = this.props.storage.tableAPI?.getState().rowCount === 0;

        if (disableAll || action || isEmpty) {
            return [...disabledButtons, ...unorganizedButtons];
        }

        return disabledButtons;
    }

    createToolbarItem(id: UnorganizedAssetAction, iconName: string, order: number, args: ICustomButtonDefArgs, forceDisabled = false): TTableToolbarItem {
        const action = this.getTableAction();
        const isDisabled = (!!action && action !== id) || args.isDisabled || forceDisabled;
        const isActive = action === id;
        return {
            itemType: ToolbarItemType.Icon,
            id,
            label: this.props.storage.t(`FixedAsset:Unorganized.${id}`),
            iconName,
            isDisabled,
            isActive,
            order
        };
    }

    getCustomButtonDef(action: TableButtonsActionType, args: ICustomButtonDefArgs): TTableToolbarItem {
        switch (action) {
            case UnorganizedAssetAction.AddToCosts:
                return this.createToolbarItem(UnorganizedAssetAction.AddToCosts, "Costs", TableActionOrder.Add + 3, args);
            case UnorganizedAssetAction.Create:
                return this.createToolbarItem(UnorganizedAssetAction.Create, "Add", TableActionOrder.Add + 1, args);
            case UnorganizedAssetAction.Complete:
                return this.createToolbarItem(UnorganizedAssetAction.Complete, "AddTo", TableActionOrder.Add + 2, args);
            default:
                return super.getCustomButtonDef(action, args);
        }
    }

    setTableAction = async (action: UnorganizedAssetAction = null, active = true): Promise<void> => {
        await super.setTableAction(action, active);
        this.forceUpdate();
    };

    get isCbaCompany(): boolean {
        return isCashBasisAccountingCompany(this.props.storage.context);
    }

    get isInEditableWindow(): boolean {
        return !!this.props.rootStorage;
    }

    getRootStorage = (): FormStorage<IAssetEntity, IFixedAssetCustomData> => {
        return this.props.rootStorage as FormStorage<IAssetEntity, IFixedAssetCustomData>;
    };

    getEditableWindowActionType = memoizeOneWithParamsCheck((): FixedAssetFormViewAction => {
        return this.getRootStorage()?.getCustomData().FixedAssetFormViewAction;
    });

    get isAddFromUnsorted(): boolean {
        return this.getEditableWindowActionType() === FixedAssetFormViewAction.AddFromUnsorted;
    }

    get rootStorageAssetType(): AssetTypeCode {
        return (this.getRootStorage()?.data.entity as IAssetEntity)?.Type?.Code as AssetTypeCode;
    }

    get assetType(): AssetTypeCode {
        return this.state.selectedSecondaryFilter as AssetTypeCode;
    }

    getSelectedAssets = async (): Promise<IUnorganizedAssetEntity[]> => {
        const rows = this.props.storage.tableAPI.getState().rows;
        const selectedRows = await this.props.storage.tableAPI.getAffectedRows();
        return selectedRows.map(item => getRow(rows, item.bc).customData.entity as IUnorganizedAssetEntity);
    };

    getEntryIds = (rows: IUnorganizedAssetEntity[]): number[] => rows.map(item => item.DocumentItem.Id);

    handleToolbarConfirm = async (): Promise<void> => {
        const selectedAssets = await this.getSelectedAssets();

        if (this.isAddFromUnsorted) {
            // We do pairing immediately because we already have AssetId
            // Note: only pairing action is currently used this way
            const rootStorage = this.getRootStorage();
            const entryIds = this.getEntryIds(selectedAssets);
            const assetId = rootStorage.data.bindingContext.getKey();
            let isSuccess = false;
            let entity = null;
            // todo: check how alerts are displayed here and unify may be??
            this.props.setBusy(true);
            let result;
            if (rootStorage.data.bindingContext.isNew()) {
                // creates new asset with paired documentItems
                result = await createAssetFromEntries(entryIds, rootStorage.data.entity as IAssetEntity);
                entity = result.data;
            } else {
                // complete existing
                result = await pairDocumentItemsWithAsset(entryIds, assetId);
            }
            this.props.setBusy(false);
            this.props.storage.setTableAlert(result.alert);
            isSuccess = result.alert.status === Status.Success;

            if (entity || isSuccess) {
                rootStorage.emitter.emit(FixedAssetEditableWindowActionFinish, entity);

                // turn off table action, reloads table, etc...
                await this.handleCustomDialogClose();
            }
        } else {
            this._selectedRows = selectedAssets;
            this.forceUpdate();
        }
    };

    handleToolbarCancel = (): void => {
        if (this.isTableAction()) {
            this.props.storage.tableAPI.cancelAction();
            this._selectedRows = [];
            this._amount = 0;
            this.setTableAction(null);
        }
    };

    getConfirmText(tableAction: TableButtonsAction | string): string | React.ReactElement {
        const { storage } = this.props;
        const activeRowActionCount = storage.tableAPI?.getState().activeRows.size;

        if ([UnorganizedAssetAction.Create, UnorganizedAssetAction.Complete, UnorganizedAssetAction.AddToCosts].includes(tableAction as UnorganizedAssetAction)) {
            return getConfirmationActionText(storage.t(`FixedAsset:Unorganized.${tableAction}`), activeRowActionCount);
        }

        return super.getConfirmText(tableAction);
    }

    getActionText(action: UnorganizedAssetAction, type: "Confirm" | "Title"): string {
        const { storage } = this.props;
        return storage.t(`FixedAsset:Unorganized.${action}${type}`);
    }

    createAndInitFormStorage = (entitySet: EntitySetName, prepareNumberRange = false): FormStorage => {
        const { storage } = this.props;
        const bindingContext = createBindingContext(entitySet, storage.oData.metadata);
        const definition = getDefByEntityType(getEntityTypeFromBindingContext(bindingContext))(storage.context);
        // create form definition from massEditable fields' def merged with form group definitions
        const formDef = definition.form;

        const formStorage = new FormStorage({
            oData: storage.oData,
            history: storage.history,
            t: storage.t,
            context: storage.context,
            id: formDef.id,
            definition: formDef,
            refresh: () => {
                this._actionDialogRef.current?.forceUpdate();
            }
        });

        formStorage.init({
            bindingContext: bindingContext.addKey(NEW_ITEM_DETAIL, true),
            data: {}, // just empty form without default values
            ignoreDefaults: true
        })
            .then(() => prepareNumberRange && setMatchingNumberRange(formStorage))
            .then(() => formStorage.refresh());

        return formStorage;
    };

    getSelectedRowsTitle = (): ReactElement => {
        return getConfirmationActionText(this.props.storage.t("FixedAsset:Unorganized.SelectedRecords"), this._selectedRows.length);
    };

    renderSelectedRowsTable = (withoutMargin = false): ReactElement => {
        const tableState = this.props.storage.tableAPI.getState();
        // Table columns
        const columns: IColumn[] = tableState.columns.map(column => ({
            id: column.id,
            textAlign: column.textAlign,
            label: column.label
        }));

        const rows = this.props.storage.tableAPI.getRowsArray().filter(row =>
            this._selectedRows.find(selectedRow => selectedRow.DocumentItem.Id === row.customData?.entity.DocumentItem.Id));

        return (
            <ScrollBar style={{
                overflowX: "visible",
                width: "100%",
                height: "auto",
                marginBottom: withoutMargin ? 0 : "35px"
            }}>
                <SimpleTable columns={columns} rows={rows}/>
            </ScrollBar>
        );
    };

    renderSelectedRowsCollapsibleSection = (): ReactElement => {
        return (
            <CollapsibleSection title={this.getSelectedRowsTitle()}
                                defaultIsOpen={false}
                                removeBodyPadding
                                style={{
                                    marginBottom: "35px",
                                    width: "100%"
                                }}>
                {this.renderSelectedRowsTable(true)}
            </CollapsibleSection>
        );
    };

    renderCustomDialog = (): ReactElement => {
        const action = this.getTableAction();
        if (!this._selectedRows?.length || !action) {
            return null;
        }
        const ids = this.getEntryIds(this._selectedRows);

        let storage: FormStorage;

        switch (action) {
            case UnorganizedAssetAction.Complete:
                // editable window with fixed asset selection
                return (
                    <CompleteFixedAsset storage={this.props.storage}
                                        renderedTable={this.renderSelectedRowsTable()}
                                        type={this.assetType}
                                        entryIds={ids}
                                        onClose={this.handleCustomDialogClose}/>
                );
            case UnorganizedAssetAction.Create:
                storage = this.createAndInitFormStorage(EntitySetName.Assets, true);
                return (
                    <CreateNewAssetDialog ref={this._actionDialogRef}
                                          title={this.getActionText(action, "Title")}
                                          confirmText={this.getActionText(action, "Confirm")}
                                          onClose={this.handleCustomDialogClose}
                                          storage={storage}
                                          entryIds={ids}>
                        {this.renderSelectedRowsCollapsibleSection()}
                    </CreateNewAssetDialog>
                );
            case UnorganizedAssetAction.AddToCosts:
                const bindingContext = createBindingContext(BindingContext.localContext("AddToCosts"), this.props.storage.oData.metadata);
                const initialData = {
                    customData: {
                        header: this.renderSelectedRowsCollapsibleSection(),
                        documentItemIds: ids
                    }
                };
                const maxEntryDate = this.getMaxSelectedEntryDate(this._selectedRows);
                const validator = (value: TValue, args: IGetValueArgs): boolean | ValidationError =>
                        getValidationResult(getInActiveFYValidation(value, args), getMaxJournalEntryDateValidation(maxEntryDate)(value, args));
                return (
                    <PlugAndPlayForm
                        getDef={getAddToCostsDefFactory(validator)}
                        t={this.props.storage.t}
                        bindingContext={bindingContext}
                        initialData={initialData}
                        formView={AddToCostsDialogFormView}
                        formViewProps={{
                            dialogProps: {
                                title: this.props.storage.t("FixedAsset:AddToCosts.Title")
                            },
                            formProps: {
                                renderScrollbar: false
                            },
                            onAfterConfirm: this.handleAddToCostsConfirm,
                            onClose: () => this.handleCustomDialogClose(false)
                        }}
                    />
                );
            default:
                return null;
        }
    };

    handleAddToCostsConfirm = async (): Promise<void> => {
        // success message
        const links = this._selectedRows.map((entity) => {
            const url = getDetailRouteByEntityType(entity.Document.DocumentType.Code as unknown as EntityTypeName, entity.Document.Id, true);
            return (
                <Clickable link={url} isLink>{entity.Document.NumberOurs}</Clickable>
            );
        });
        const linkNodes = joinReactNodes(links, ", ");
        const alert = {
            status: Status.Success,
            title: i18next.t("Common:Validation.SuccessTitle"),
            subTitle: (<>{this.props.storage.t("FixedAsset:AddToCosts.SuccessMessage")}: {linkNodes}</>)
        };
        this.props.storage.setTableAlert(alert);

        await this.handleCustomDialogClose(true);
    };

    handleCustomDialogClose = async (reloadTable = true): Promise<void> => {
        const { storage } = this.props;
        const { tableAPI } = storage;
        // remove breadcrumbs, which might be added in editable window
        storage.context.setViewBreadcrumbs({ items: [], lockable: false });
        await tableAPI.confirmAction();
        this._selectedRows = [];
        this._amount = 0;
        this.setTableAction(null);
        if (reloadTable) {
            await tableAPI.reloadTable();
        }
    };

    headerData = (entity: IAssetEntity): IReadOnlyListItem[] => {
        const storage = this.props.rootStorage;
        const info = storage.getInfo(storage.data.bindingContext.navigate("Name"));
        return [{
            label: info.label,
            value: entity.Name
        }, {
            label: storage.t("MinorAsset:Form.Status"),
            value: i18next.t(`MinorAsset:Status.${entity.Status?.Code}`).toString()
        }, {
            label: storage.t("FixedAsset:Form.CalculatedPrice"),
            value: assetPriceFormatter(entity.CalculatedPrice, { entity })
        }];
    };

    getMaxSelectedEntryDate = (selectedRows: IUnorganizedAssetEntity[]): Dayjs => {
        // calculate maximal TransactionDate
        //  - DateOfTransaction in AddToCosts dialog needs to be later
        let maxDate: Dayjs;
        selectedRows.forEach((row) => {
            if (!maxDate || maxDate.isBefore(row.EntryDate)) {
                maxDate = getUtcDayjs(row.EntryDate);
            }
        });
        return maxDate;
    };

    renderAssetHeader = (): ReactElement => {
        if (this.isAddFromUnsorted) {
            const storage = this.props.rootStorage;
            const entity = storage.data.entity as IAssetEntity;
            return (<>
                <ReadOnlyList title={`${this.props.rootStorage.t("FixedAsset:FormTitle")} ${entity.NumberOurs}`}
                              data={this.headerData(entity)} />
                <SecondaryTableViewTitle>
                    {storage.t("FixedAsset:Unorganized.TitleCompletion")}
                </SecondaryTableViewTitle>
            </>);
        } else {
            return (
                    <Header title={this.props.title}
                            hotspotId={"tableHeader"}
                            afterIconsContent={this.props.summary} />
            );
        }
    };

    handleActiveRowActionCountChange = async (count: number): Promise<void> => {
        this.props.onActiveRowActionCountChange?.(count);

        const rows = this.props.storage.tableAPI.getState().rows;
        const selectedRows = await this.props.storage.tableAPI.getAffectedRows();
        this._amount = selectedRows.reduce((sum: number, item) => {
            const row = getRow(rows, item.bc);
            const values = row.customData.entity as IUnorganizedAssetEntity;
            return sum + values.Amount;
        }, 0);
        // refresh would reload secondary query filters, we just need to rerender confirmation buttons
        this.forceUpdate();
    };

    getStaticToolbarItems = (): TToolbarItem[] => {
        if (!this.getTableAction()) {
            return [];
        }
        const formattedAmount = formatCurrency(this._amount, getCompanyCurrency(this.props.storage.context));
        return [(
            <ToolbarStaticText key={"summary"}>
                {this.props.storage.t("FixedAsset:Unorganized.SumOfSelected")}: <ColoredText
                color="C_SEM_text_warning"><strong>{formattedAmount}</strong></ColoredText>
            </ToolbarStaticText>
        )];
    };

    renderTabs = (): ReactElement => {
        if (!!this.rootStorageAssetType || this.isCbaCompany) {
            // Type is preselected by the asset (rendered in editable window on top of asset form)
            // we also don't render tabs in CBA as it is not solved yet
            // -> do not render the tabs at all (but filter query is still applied)
            return null;
        }

        return super.renderTabs();
    };

    renderFooter(): React.ReactElement {
        return (
                <ButtonGroupStyled align={TextAlign.Right}>
                    {this.props.buttons}
                </ButtonGroupStyled>
        );
    }

    getTableSharedProps(): ISmartTableProps & { key: string } {
        const props = super.getTableSharedProps();
        props.keepActiveRowsOnFilterChange = this.isInEditableWindow;
        return props;
    }

    render = () => {
        if (!this.isLoaded()) {
            return null;
        }

        return (
            <>
                {this.shouldRenderBreadcrumbs &&
                    <BreadCrumbProvider back={this.props.back ?? this.props.storage?.initialHistoryState?.back}
                                        customBreadCrumbs={this.props.storage?.initialHistoryState?.breadCrumbs}
                                        removeLast={!!this.props.storage.data.rowBindingContext}/>}
                <View scrollProps={{ ref: this._scrollBarInstanceRef }}
                      {...this.getViewProps()}
                      fixedFooter={this.renderFooter()}
                      hotspotContextId={this.props.storage.id}>
                    <TopWrapper _loaded={this.isLoaded()}>
                        {this.isInEditableWindow ? this.renderAssetHeader() : this.renderSmartHeader()}
                        {this.renderFilterBar()}
                        {this.renderAlert()}
                        {this.renderTabs()}
                        {(!this.isInEditableWindow || this.isAddFromUnsorted) && this.renderToolbar()}
                    </TopWrapper>
                    {this.renderTable()}
                    {this.renderCustomDialog()}
                    {this.renderDefaultDialogs()}
                </View>
            </>
        );
    };
}

export default withAuthContext(withBusyIndicator({ isDelayed: true })(UnorganizedAssetTableView));
